import { identity } from "lodash-es";

import { distinct } from "../../graph/aggregate.ts";
import type { ConnectedNode, Reducer, Reducers } from "../../graph/types.ts";
import { isConnectedNode, isNode } from "../../graph/types.ts";
import type {
  GrantAggregates,
  MetaGrantAggregates,
} from "../../types/assessment/data/grant.ts";
import type {
  AssessmentNodes,
  Usage,
} from "../../types/assessment/data/index.ts";
import { Usages } from "../../types/assessment/data/index.ts";
import { keyAggregate, privilegeAggregate } from "./shared.ts";

export const EMPTY_USAGE = Object.fromEntries(
  Usages.map((u) => [u, 0] as const)
) as Record<Usage, number>;

const usageCounts: Reducer<
  AssessmentNodes,
  Record<Usage, number>,
  Record<Usage, number> | undefined,
  Record<Usage, number>
> = {
  finalize: identity,
  initialize: () => ({ ...EMPTY_USAGE }),
  reduce: (memo, value) => {
    for (const u of Usages) {
      memo[u] = value?.[u] ?? 0;
    }
  },
  span: () => [], // never descend
  toValue: (node) =>
    isNode("grant")
      ? (Object.fromEntries(
          (node as ConnectedNode<AssessmentNodes, "grant">).children
            .filter(isConnectedNode("usage"))
            .map((n) => [n.data.type, n.data.count ?? 0] as const)
        ) as Record<Usage, number>)
      : undefined,
};

export const grantReducers: Reducers<AssessmentNodes, GrantAggregates> = {
  privileges: privilegeAggregate,
  principals: keyAggregate("identity"),
  risks: keyAggregate("risk"),
  usages: usageCounts,
};

const lateralByGrant = distinct<
  AssessmentNodes,
  "lateral",
  AssessmentNodes["lateral"]
>("lateral", ({ data }) => data, identity);

export const metaGrantReducers: Reducers<AssessmentNodes, MetaGrantAggregates> =
  {
    laterals: lateralByGrant,
    privileges: privilegeAggregate,
  };
